import { useEffect, useRef, useState } from "react";
import { useNavigate } from 'react-router-dom';
const COLORS = ["#339966","#8cd9b3","#f59e0b","#10b981","#f472b6","#38bdf8","#fb923c","#34d399"];
const SHAPES = ["rect","circle","diamond"];

function rand(a, b) { return a + Math.random() * (b - a); }

function useConfetti(canvasRef) {
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const W = canvas.offsetWidth;
    const H = canvas.offsetHeight;
    canvas.width = W;
    canvas.height = H;

    class Piece {
      constructor(burst = false) {
        this.color = COLORS[Math.floor(Math.random() * COLORS.length)];
        this.shape = SHAPES[Math.floor(Math.random() * SHAPES.length)];
        this.w = rand(7, 13);
        this.h = rand(4, 9);
        this.rot = rand(0, Math.PI * 2);
        this.vr = rand(-0.12, 0.12);
        this.opacity = rand(0.75, 1);
        this.alive = true;
        if (burst) {
          this.x = rand(W * 0.2, W * 0.8);
          this.y = rand(80, 200);
          this.vx = rand(-7, 7);
          this.vy = rand(-9, -2);
          this.delay = Math.floor(rand(0, 30));
        } else {
          this.x = rand(0, W);
          this.y = rand(-30, -10);
          this.vx = rand(-1.5, 1.5);
          this.vy = rand(2, 4);
          this.delay = 0;
        }
      }
      update() {
        if (this.delay > 0) { this.delay--; return; }
        this.x += this.vx;
        this.y += this.vy;
        this.rot += this.vr;
        this.vy += 0.045;
        this.vx *= 0.997;
        if (this.y > H + 20) this.alive = false;
      }
      draw() {
        if (this.delay > 0) return;
        ctx.save();
        ctx.globalAlpha = this.opacity;
        ctx.translate(this.x, this.y);
        ctx.rotate(this.rot);
        ctx.fillStyle = this.color;
        if (this.shape === "rect") {
          ctx.fillRect(-this.w / 2, -this.h / 2, this.w, this.h);
        } else if (this.shape === "circle") {
          ctx.beginPath();
          ctx.arc(0, 0, this.w / 2, 0, Math.PI * 2);
          ctx.fill();
        } else {
          ctx.beginPath();
          ctx.moveTo(0, -this.h / 2);
          ctx.lineTo(this.w / 2, 0);
          ctx.lineTo(0, this.h / 2);
          ctx.lineTo(-this.w / 2, 0);
          ctx.closePath();
          ctx.fill();
        }
        ctx.restore();
      }
    }

    let pieces = [];
    let frame = 0;
    let rafId;

    for (let i = 0; i < 160; i++) pieces.push(new Piece(true));

    function loop() {
      frame++;
      ctx.clearRect(0, 0, W, H);
      if (frame > 20 && frame < 220 && frame % 5 === 0) {
        pieces.push(new Piece(false));
      }
      pieces = pieces.filter((p) => p.alive);
      pieces.forEach((p) => { p.update(); p.draw(); });
      if (pieces.length > 0 || frame < 220) {
        rafId = requestAnimationFrame(loop);
      }
    }
    loop();
    return () => cancelAnimationFrame(rafId);
  }, []);
}

const ORDER_ITEMS = [
  { emoji: "👟", name: "Air Runner Pro",      meta: "Size 42 · White · ×1", price: "€129.00", bg: "#eef2ff" },
  { emoji: "🎽", name: "Sport Tee — Forest",  meta: "Size M · ×2",           price: "€58.00",  bg: "#f0fdf4" },
  { emoji: "🧢", name: "Classic Cap",         meta: "Black · ×1",            price: "€24.00",  bg: "#fefce8" },
];

const STEPS = ["Placed", "Packing", "Shipped", "Delivered"];

const css = `
@import url('https://fonts.googleapis.com/css2?family=Quicksand:wght@300;400;500;600;700&display=swap');
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

@keyframes oc-bounceIn {
  0%   { opacity: 0; transform: scale(0.3) rotate(-10deg); }
  55%  { opacity: 1; transform: scale(1.1) rotate(2deg); }
  75%  { transform: scale(0.94); }
  100% { opacity: 1; transform: scale(1) rotate(0deg); }
}
@keyframes oc-float {
  0%, 100% { transform: translateY(0px); }
  50%       { transform: translateY(-8px); }
}
@keyframes oc-ringOut {
  0%   { transform: scale(1); opacity: 0.65; }
  100% { transform: scale(2.7); opacity: 0; }
}
@keyframes oc-circleDraw {
  from { stroke-dashoffset: 314; }
  to   { stroke-dashoffset: 0;   }
}
@keyframes oc-checkDraw {
  from { stroke-dashoffset: 66; }
  to   { stroke-dashoffset: 0;  }
}
@keyframes oc-fadeUp {
  from { opacity: 0; transform: translateY(22px); }
  to   { opacity: 1; transform: translateY(0);    }
}
@keyframes oc-slideLeft {
  from { opacity: 0; transform: translateX(-18px); }
  to   { opacity: 1; transform: translateX(0);     }
}
@keyframes oc-dotBounce {
  0%, 100% { transform: scale(1);    }
  50%       { transform: scale(1.45); }
}
@keyframes oc-pillPop {
  0%   { opacity: 0; transform: scale(0.7);  }
  70%  { transform: scale(1.06); }
  100% { opacity: 1; transform: scale(1);    }
}

.oc-page {
  font-family: 'Quicksand', sans-serif;
  background: #ffffff;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 56px 20px 72px;
  position: relative;
  overflow: hidden;
}
.oc-canvas {
  position: absolute; top: 0; left: 0;
  width: 100%; height: 100%;
  pointer-events: none; z-index: 10;
}
.oc-orb1 {
  position: absolute; width: 500px; height: 500px; border-radius: 50%;
  background: radial-gradient(circle, #d9f2e6 0%, transparent 70%);
  top: -160px; right: -160px; pointer-events: none; opacity: 0.7;
}
.oc-orb2 {
  position: absolute; width: 340px; height: 340px; border-radius: 50%;
  background: radial-gradient(circle, #00e6ac 0%, transparent 70%);
  bottom: -80px; left: -80px; pointer-events: none; opacity: 0.6;
}

.oc-icon-wrap {
  position: relative; width: 110px; height: 110px;
  margin-bottom: 26px; z-index: 2;
  opacity: 0;
  animation: oc-bounceIn 0.75s cubic-bezier(0.34,1.4,0.64,1) 0.5s forwards;
}
.oc-icon-wrap.oc-floating {
  animation:
    oc-bounceIn 0.75s cubic-bezier(0.34,1.4,0.64,1) 0.5s forwards,
    oc-float 3.2s ease-in-out 1.4s infinite;
}
.oc-ring {
  position: absolute; inset: -10px; border-radius: 50%;
  border: 2px solid #39ac73;
  animation: oc-ringOut 2s ease-out 1.1s infinite;
}
.oc-ring2 { animation-delay: 1.55s; border-color: #e65c00; }
.oc-icon-bg {
  position: absolute; inset: 0; border-radius: 50%;
  background: linear-gradient(145deg, #339933, #9fdfbf);
}
.oc-icon-svg { position: absolute; inset: 0; width: 100%; height: 100%; }

.oc-tag {
  font-size: 11px; font-weight: 700; letter-spacing: 0.2em;
  color: #8cd9b3; text-transform: uppercase; z-index: 2;
  opacity: 0; animation: oc-fadeUp 0.5s ease 1.05s forwards;
  margin-bottom: 8px;
}
.oc-heading {
  font-size: 32px; font-weight: 700; color: #1a1a2e;
  text-align: center; line-height: 1.2; z-index: 2;
  opacity: 0; animation: oc-fadeUp 0.55s ease 1.15s forwards;
}
.oc-heading span { color: #339966; }
.oc-sub {
  font-size: 13px; font-weight: 500; color: #b0b7c3;
  text-align: center; margin-top: 8px; z-index: 2;
  opacity: 0; animation: oc-fadeUp 0.5s ease 1.26s forwards;
  letter-spacing: 0.02em;
}
.oc-pill {
  display: inline-flex; align-items: center; gap: 7px;
  background: #f5f3ff; border: 1.5px solid #ddd6fe;
  border-radius: 100px; padding: 7px 18px;
  font-size: 11px; font-weight: 700; color: #339966;
  letter-spacing: 0.1em; margin-top: 14px; z-index: 2;
  opacity: 0; animation: oc-pillPop 0.5s cubic-bezier(0.34,1.5,0.64,1) 1.38s forwards;
}
.oc-pill-dot {
  width: 7px; height: 7px; border-radius: 50%; background: #339966;
  animation: oc-dotBounce 1.5s ease infinite;
}

.oc-card {
  width: 100%; max-width: 420px; border-radius: 22px;
  border: 1.5px solid #ede9fe; background: #fff;
  overflow: hidden; margin-top: 30px; z-index: 2;
  opacity: 0; animation: oc-fadeUp 0.6s ease 1.5s forwards;
}
.oc-card-header {
  background: linear-gradient(130deg, #339966 0%, #8cd9b3 100%);
  padding: 22px 24px 20px; position: relative; overflow: hidden;
}
.oc-card-header::after {
  content: ''; position: absolute; right: -30px; top: -30px;
  width: 120px; height: 120px; border-radius: 50%;
  background: rgba(255,255,255,0.08);
}
.oc-card-header::before {
  content: ''; position: absolute; right: 30px; bottom: -40px;
  width: 90px; height: 90px; border-radius: 50%;
  background: rgba(255,255,255,0.06);
}
.oc-ch-label {
  font-size: 10px; font-weight: 600; letter-spacing: 0.18em;
  color: rgba(255,255,255,0.65); text-transform: uppercase; margin-bottom: 4px;
}
.oc-ch-amount { font-size: 30px; font-weight: 700; color: #fff; }
.oc-ch-tags { display: flex; gap: 8px; margin-top: 12px; flex-wrap: wrap; }
.oc-ch-tag {
  background: rgba(255,255,255,0.18); border-radius: 100px;
  padding: 4px 12px; font-size: 10px; font-weight: 600;
  color: #fff; letter-spacing: 0.06em;
}

.oc-items { padding: 4px 20px 0; }
.oc-item {
  display: flex; align-items: center; gap: 14px;
  padding: 13px 0; border-bottom: 1px solid #f8f6ff;
  opacity: 0;
}
.oc-item:last-child { border: none; }
.oc-item-img {
  width: 46px; height: 46px; border-radius: 13px;
  display: flex; align-items: center; justify-content: center;
  font-size: 21px; flex-shrink: 0;
}
.oc-item-name  { font-size: 13px; font-weight: 700; color: #1a1a2e; margin-bottom: 2px; }
.oc-item-meta  { font-size: 11px; font-weight: 500; color: #c4b5fd; }
.oc-item-price { font-size: 14px; font-weight: 700; color: #1a1a2e; margin-left: auto; flex-shrink: 0; }

.oc-totals { padding: 12px 20px 18px; border-top: 1.5px dashed #ede9fe; margin-top: 2px; }
.oc-t-row {
  display: flex; justify-content: space-between;
  font-size: 12px; font-weight: 500; color: #b0b7c3; margin-bottom: 6px;
}
.oc-t-row.grand {
  font-size: 15px; font-weight: 700; color: #1a1a2e;
  margin-top: 10px; padding-top: 10px;
  border-top: 1.5px solid #f0eeff; margin-bottom: 0;
}
.oc-t-row.grand span:last-child { color: #339966; font-size: 17px; }
.oc-free   { color: #10b981; font-weight: 700; }
.oc-saving { color: #f59e0b; font-weight: 700; }

.oc-grid {
  display: grid; grid-template-columns: 1fr 1fr; gap: 10px;
  width: 100%; max-width: 420px; margin-top: 10px; z-index: 2;
  opacity: 0; animation: oc-fadeUp 0.5s ease 1.66s forwards;
}
.oc-box {
  border-radius: 16px; background: #fafafe;
  border: 1.5px solid #ede9fe; padding: 14px 16px;
}
.oc-box-label {
  font-size: 9px; font-weight: 700; letter-spacing: 0.18em;
  color: #8cd9b3; text-transform: uppercase; margin-bottom: 5px;
}
.oc-box-val { font-size: 12px; font-weight: 600; color: #1a1a2e; line-height: 1.55; }

.oc-tracker {
  width: 100%; max-width: 420px; margin-top: 10px; z-index: 2;
  border-radius: 18px; background: #fafafe;
  border: 1.5px solid #ede9fe; padding: 18px 20px;
  opacity: 0; animation: oc-fadeUp 0.5s ease 1.78s forwards;
}
.oc-tr-title {
  font-size: 9px; font-weight: 700; letter-spacing: 0.18em;
  color: #8cd9b3; text-transform: uppercase; margin-bottom: 16px;
}
.oc-tr-steps { display: flex; align-items: center; }
.oc-tr-step  { display: flex; flex-direction: column; align-items: center; gap: 7px; }
.oc-tr-dot   { width: 11px; height: 11px; border-radius: 50%; background: #ede9fe; }
.oc-tr-dot.done   { background: #339966; }
.oc-tr-dot.active {
  background: #339966; box-shadow: 0 0 0 5px rgba(108,99,255,0.18);
  animation: oc-dotBounce 1.5s ease infinite;
}
.oc-tr-label      { font-size: 9px; font-weight: 600; color: #c4b5fd; white-space: nowrap; }
.oc-tr-label.done { color: #339966; }
.oc-tr-label.now  { color: #1a1a2e; font-weight: 700; }
.oc-tr-line       { flex: 1; height: 2px; border-radius: 2px; background: #ede9fe; margin-bottom: 16px; }
.oc-tr-line.done  { background: linear-gradient(90deg, #339966, #8cd9b3); }

.oc-cta {
  display: flex; gap: 10px;
  width: 100%; max-width: 420px; margin-top: 20px; z-index: 2;
  opacity: 0; animation: oc-fadeUp 0.5s ease 1.9s forwards;
}
.oc-btn-primary {
  flex: 1; background: linear-gradient(130deg, #339966, #8cd9b3);
  color: #fff; border: none; border-radius: 13px; padding: 15px;
  font-family: 'Quicksand', sans-serif; font-size: 13px; font-weight: 700;
  cursor: pointer; letter-spacing: 0.03em;
  transition: opacity 0.15s, transform 0.15s;
}
.oc-btn-primary:hover { opacity: 0.87; transform: translateY(-2px); }
.oc-btn-secondary {
  background: #fff; color: #339966;
  border: 1.5px solid #ddd6fe; border-radius: 13px;
  padding: 15px 22px; font-family: 'Quicksand', sans-serif;
  font-size: 13px; font-weight: 600; cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}
.oc-btn-secondary:hover { background: #f5f3ff; border-color: #8cd9b3; }
`;

export default function OrderSuccess() {
    const navigate = useNavigate();
  const canvasRef = useRef(null);
  const [floating, setFloating] = useState(false);

  useConfetti(canvasRef);

  useEffect(() => {
    const t = setTimeout(() => setFloating(true), 1400);
    setTimeout(() => navigate('/Orders'), 5800);
    return () => clearTimeout(t);
  }, []);

  return (
    <>
      <style>{css}</style>
      <div className="oc-page">
        <canvas ref={canvasRef} className="oc-canvas" />
        <div className="oc-orb1" />
        <div className="oc-orb2" />

        {/* Animated checkmark icon */}
        <div className={`oc-icon-wrap${floating ? " oc-floating" : ""}`}>
          <div className="oc-ring" />
          <div className="oc-ring oc-ring2" />
          <div className="oc-icon-bg" />
          <svg className="oc-icon-svg" viewBox="0 0 110 110" fill="none">
            <circle
              cx="55" cy="55" r="49"
              stroke="rgba(255,255,255,0.28)" strokeWidth="1.5"
              strokeDasharray="314" strokeDashoffset="314"
              style={{
                animation: "oc-circleDraw 0.9s ease 0.55s forwards",
                transformOrigin: "center",
                transform: "rotate(-90deg)",
              }}
            />
            <path
              d="M35 55 L49 69 L75 39"
              stroke="#fff" strokeWidth="4.5"
              strokeLinecap="round" strokeLinejoin="round"
              fill="none" strokeDasharray="66" strokeDashoffset="66"
              style={{ animation: "oc-checkDraw 0.45s ease 1.1s forwards" }}
            />
          </svg>
        </div>

        {/* Hero copy */}
        <p className="oc-tag">Success!</p>
        <h1 className="oc-heading">Order <span>placed</span> successfully 🎉</h1>
        <p className="oc-sub">We've got your order and we're on it 🚀</p>
       
        {/* Receipt card */}
        <div className="oc-card">
          

         

        
        </div>

       

       

        {/* CTAs */}
        <div className="oc-cta">
          <button className="oc-btn-primary" onClick={(e)=>navigate('/Orders')}>Track my order</button>
          <button className="oc-btn-secondary" onClick={(e)=>navigate('/Shop/all')}>Shop more</button>
        </div>
      </div>
    </>
  );
}